import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { MdDragIndicator, MdGridView, MdViewList } from "react-icons/md";
import { commonToast } from "../helper/Toast";
import {
  buildCardLinks,
  persistDeckCardSortOrder,
  readLocalCardOrder,
  reorderCardLinks,
  sortCardsByDeckOrder,
  writeLocalCardOrder,
} from "../helper/deckCardOrder";
import Loading from "./Loading";
import QuestionCard from "./QuestionCard";

export default function DeckCardsPanel({
  cards = [],
  links = [],
  deckId,
  enableReorder = false,
  onOrderSaved,
  storageKey = "deck_cards_view",
  toolbar = null,
  selectMode = false,
  selectedCardIds = [],
  onSelect,
  onEdit,
  onDelete,
  onDetail,
  showHeader = true,
  showAnswer = true,
  showHints = true,
  showAiBadges = true,
  emptyLabel,
  loading = false,
}) {
  const { t } = useTranslation();
  const dragFromRef = useRef(null);
  const dragOverRef = useRef(null);
  const dropHandledRef = useRef(false);
  const idsRef = useRef([]);
  const lastLinksRef = useRef(null);
  const persistTailRef = useRef(Promise.resolve());
  const linksRef = useRef(links);
  const cardsRef = useRef(cards);
  linksRef.current = links;
  cardsRef.current = cards;

  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === "list" ? "list" : "card";
    } catch {
      return "card";
    }
  });
  const [orderNotice, setOrderNotice] = useState(null);
  const [localOrder, setLocalOrder] = useState(() => readLocalCardOrder(deckId));

  useEffect(() => {
    lastLinksRef.current = null;
    setLocalOrder(readLocalCardOrder(deckId));
  }, [deckId]);

  const sortedCards = useMemo(
    () => sortCardsByDeckOrder(cards, links),
    [cards, links]
  );

  const visibleCards = useMemo(() => {
    if (!localOrder?.length) return sortedCards;
    const byId = new Map(cards.map((card) => [card.cardId, card]));
    const ordered = [];
    const seen = new Set();
    localOrder.forEach((id) => {
      const card = byId.get(id);
      if (!card || seen.has(id)) return;
      seen.add(id);
      ordered.push(card);
    });
    cards.forEach((card) => {
      if (!seen.has(card.cardId)) ordered.push(card);
    });
    return ordered;
  }, [cards, localOrder, sortedCards]);

  idsRef.current = visibleCards.map((card) => card.cardId);

  useEffect(() => {
    if (orderNotice !== "saved") return undefined;
    const timer = setTimeout(() => setOrderNotice(null), 1600);
    return () => clearTimeout(timer);
  }, [orderNotice]);

  const changeView = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(storageKey, mode);
    } catch {
      // storage may be unavailable
    }
  };

  const complexityFor = (card) => {
    const meta = (links || []).find((link) => link.cardId === card.cardId);
    return meta?.complexity ?? card?.complexity;
  };

  const queuePersist = (previousLinks, nextLinks) => {
    const savedDeckId = deckId;
    persistTailRef.current = persistTailRef.current
      .catch(() => {})
      .then(async () => {
        try {
          await persistDeckCardSortOrder(savedDeckId, previousLinks, nextLinks);
          if (savedDeckId === deckId) await onOrderSaved?.();
          setOrderNotice("saved");
        } catch (error) {
          if (error?.code === "LOGIN_REQUIRED") return;
          commonToast(
            error?.data?.errorDescription ||
              error?.data?.error ||
              error?.message ||
              t("cardOrderFailed"),
            "error"
          );
        }
      });
  };

  const handleReorder = (fromIndex, toIndex) => {
    if (!enableReorder || selectMode || !deckId) return;
    const ids = idsRef.current.slice();
    if (
      !Number.isInteger(fromIndex) ||
      !Number.isInteger(toIndex) ||
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= ids.length ||
      toIndex >= ids.length
    ) {
      return;
    }

    const previousIds = ids.slice();
    const [moved] = ids.splice(fromIndex, 1);
    ids.splice(toIndex, 0, moved);
    idsRef.current = ids;
    writeLocalCardOrder(deckId, ids);
    setLocalOrder(ids);

    if (!lastLinksRef.current || lastLinksRef.current.length !== previousIds.length) {
      const byId = new Map(cardsRef.current.map((card) => [card.cardId, card]));
      lastLinksRef.current = buildCardLinks(
        previousIds.map((id) => byId.get(id)).filter(Boolean),
        linksRef.current
      );
    }
    const previousLinks = lastLinksRef.current;
    const nextLinks = reorderCardLinks(previousLinks, fromIndex, toIndex);
    if (nextLinks === previousLinks) return;
    lastLinksRef.current = nextLinks;
    queuePersist(previousLinks, nextLinks);
  };

  const endDrag = (fromIndex, toIndex) => {
    if (dropHandledRef.current) return;
    dropHandledRef.current = true;
    handleReorder(fromIndex, toIndex);
  };

  const canReorder =
    enableReorder && viewMode === "list" && !selectMode && visibleCards.length > 1;

  const viewButtonClass = (active) =>
    `inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-sm transition-colors ${
      active
        ? "bg-white font-semibold text-gray-900 shadow-sm"
        : "font-medium text-gray-500 hover:text-gray-800"
    }`;

  return (
    <div>
      <div
        className={`mb-4 flex flex-wrap items-center gap-3 ${
          toolbar ? "justify-between" : "justify-start"
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center rounded-full bg-gray-100 p-1">
            <button
              type="button"
              aria-pressed={viewMode === "card"}
              onClick={() => changeView("card")}
              className={viewButtonClass(viewMode === "card")}
            >
              <MdGridView size={16} />
              {t("cardView")}
            </button>
            <button
              type="button"
              aria-pressed={viewMode === "list"}
              onClick={() => changeView("list")}
              className={viewButtonClass(viewMode === "list")}
            >
              <MdViewList size={16} />
              {t("listView")}
            </button>
          </div>
          {orderNotice === "saved" ? (
            <span className="text-xs font-medium text-green-600">{t("cardOrderSaved")}</span>
          ) : null}
        </div>
        {toolbar ? <div className="flex flex-wrap items-center gap-2">{toolbar}</div> : null}
      </div>

      <div
        className={
          viewMode === "list"
            ? `flex flex-col gap-2 ${selectMode && selectedCardIds.length > 0 ? "pb-20" : ""}`
            : `grid grid-cols-3 gap-3 lg:grid-cols-2 md:!grid-cols-1 ${
                selectMode && selectedCardIds.length > 0 ? "pb-20" : ""
              }`
        }
      >
        {loading && visibleCards.length === 0 ? (
          <div className="col-span-3 flex w-full flex-col items-center justify-center gap-3 py-20">
            <Loading />
            <p className="text-sm font-medium text-gray-500">{t("loading")}</p>
          </div>
        ) : visibleCards.length > 0 ? (
          visibleCards.map((item, index) =>
            viewMode === "list" ? (
              <div
                key={item.cardId || index}
                draggable={canReorder}
                onMouseDown={(event) => {
                  event.currentTarget.draggable = canReorder && !event.target.closest("[data-no-drag]");
                }}
                onDragStart={(event) => {
                  if (!canReorder || event.target.closest("[data-no-drag]")) {
                    event.preventDefault();
                    return;
                  }
                  dragFromRef.current = index;
                  dragOverRef.current = index;
                  dropHandledRef.current = false;
                  event.dataTransfer.effectAllowed = "move";
                  event.dataTransfer.setData("text/plain", String(index));
                }}
                onDragOver={(event) => {
                  if (!canReorder || dragFromRef.current == null) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                  dragOverRef.current = index;
                }}
                onDrop={(event) => {
                  if (!canReorder) return;
                  event.preventDefault();
                  const from = dragFromRef.current ?? Number(event.dataTransfer.getData("text/plain"));
                  endDrag(from, index);
                }}
                onDragEnd={() => {
                  const from = dragFromRef.current;
                  const to = dragOverRef.current;
                  dragFromRef.current = null;
                  dragOverRef.current = null;
                  if (from != null && to != null) endDrag(from, to);
                }}
                className="flex min-w-0 cursor-grab items-center gap-3 rounded-2xl border border-gray-200 bg-white px-3 py-2.5 active:cursor-grabbing"
              >
                {canReorder ? (
                  <span
                    aria-hidden
                    className="flex size-8 shrink-0 items-center justify-center rounded-lg text-gray-300"
                  >
                    <MdDragIndicator size={18} />
                  </span>
                ) : null}
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-500">
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <QuestionCard
                    item={item}
                    index={index}
                    layout="list"
                    embedded
                    complexity={complexityFor(item)}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onDetail={onDetail}
                    selectMode={selectMode}
                    selected={selectedCardIds.includes(item?.cardId)}
                    onSelect={onSelect}
                    showHeader={showHeader}
                    showAnswer={showAnswer}
                    showHints={false}
                    showAiBadges={false}
                  />
                </div>
                {canReorder ? (
                  <div className="flex shrink-0 overflow-hidden rounded-xl border border-gray-200">
                    <button
                      type="button"
                      data-no-drag
                      aria-label={t("moveCardUp")}
                      title={t("moveCardUp")}
                      disabled={index === 0}
                      onClick={() => handleReorder(index, index - 1)}
                      className="flex size-8 items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30"
                    >
                      <FiChevronUp />
                    </button>
                    <button
                      type="button"
                      data-no-drag
                      aria-label={t("moveCardDown")}
                      title={t("moveCardDown")}
                      disabled={index === visibleCards.length - 1}
                      onClick={() => handleReorder(index, index + 1)}
                      className="flex size-8 items-center justify-center border-l border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30"
                    >
                      <FiChevronDown />
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <QuestionCard
                key={item.cardId || index}
                item={item}
                index={index}
                complexity={complexityFor(item)}
                onEdit={onEdit}
                onDelete={onDelete}
                onDetail={onDetail}
                selectMode={selectMode}
                selected={selectedCardIds.includes(item?.cardId)}
                onSelect={onSelect}
                showHeader={showHeader}
                showAnswer={showAnswer}
                showHints={showHints}
                showAiBadges={showAiBadges}
              />
            )
          )
        ) : (
          <div
            className={
              viewMode === "list"
                ? "py-10 text-center text-gray-500"
                : "col-span-3 py-10 text-center text-gray-500"
            }
          >
            {emptyLabel || t("nocard")}
          </div>
        )}
      </div>
    </div>
  );
}
